import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Tabs, Toast, Modal } from '@douyinfe/semi-ui';
import { IconSearch, IconKey, IconHistogram } from '@douyinfe/semi-icons';
import Papa from 'papaparse';
import { API, copy, timestamp2string } from '../helpers';
import {
  isUsageLog,
  summarize,
  groupByDay,
  groupByModel,
  buildDetailCSVRows,
  buildModelCSVRows,
  buildDailyCSVRows,
  quotaToUSD,
} from '../helpers/usage';
import FilterBar, { DEFAULT_FILTERS, getPresetRange } from './FilterBar';
import TokenInfoCard from './TokenInfoCard';
import StatTiles from './StatTiles';
import UsageTrendPanel from './UsageTrendPanel';
import ModelStatsTable from './ModelStatsTable';
import DetailTable from './DetailTable';

const { TabPane } = Tabs;

const EMPTY_TAB = {
  balance: '未知',
  usage: '未知',
  accessdate: '未知',
  logs: [],
  tokenValid: false,
  queried: false,
};

function parseBaseUrls() {
  try {
    return JSON.parse(process.env.REACT_APP_BASE_URL) || {};
  } catch (e) {
    console.error('REACT_APP_BASE_URL 配置格式错误:', e);
    return {};
  }
}

// 下载 CSV（带 BOM，Excel 可直接打开）
function downloadCSV(rows, filename) {
  const BOM = String.fromCharCode(0xfeff);
  const csvString = BOM + Papa.unparse(rows);
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function timestampForFile() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

const LogsTable = () => {
  const baseUrls = useMemo(parseBaseUrls, []);
  const siteKeys = Object.keys(baseUrls);

  const [apikey, setAPIKey] = useState('');
  const [activeTabKey, setActiveTabKey] = useState(siteKeys[0] || '');
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });

  const baseUrl = baseUrls[activeTabKey] || '';
  const activeTabData = tabData[activeTabKey] || EMPTY_TAB;

  // ---------- 查询 ----------

  const fetchData = async (keyOverride) => {
    const key = typeof keyOverride === 'string' ? keyOverride : apikey;
    if (key === '') {
      Toast.warning('请先输入令牌，再进行查询');
      return;
    }
    if (process.env.REACT_APP_FREE_APIKEYS) {
      try {
        const freeApiKeys = JSON.parse(process.env.REACT_APP_FREE_APIKEYS);
        if (Array.isArray(freeApiKeys) && freeApiKeys.includes(key)) {
          Toast.info('免费秘钥为无限额度，不支持查询');
          return;
        }
      } catch (error) {
        console.warn('免费秘钥配置格式错误:', error);
      }
    }
    if (!/^sk-[a-zA-Z0-9]{48}$/.test(key)) {
      Toast.error('令牌格式非法！');
      return;
    }

    setLoading(true);
    try {
      const newTabData = { ...EMPTY_TAB, queried: true };

      if (process.env.REACT_APP_SHOW_BALANCE === 'true') {
        const subscription = await API.get(`${baseUrl}/v1/dashboard/billing/subscription`, {
          headers: { Authorization: `Bearer ${key}` },
        });
        newTabData.balance = subscription.data.hard_limit_usd;
        if (subscription.data.access_until !== undefined) {
          newTabData.accessdate = subscription.data.access_until; // 0 表示永不过期
        }
        newTabData.tokenValid = true;

        const now = new Date();
        const start = new Date(now.getTime() - 100 * 24 * 3600 * 1000);
        const startDate = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
        const endDate = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
        const res = await API.get(
          `${baseUrl}/v1/dashboard/billing/usage?start_date=${startDate}&end_date=${endDate}`,
          { headers: { Authorization: `Bearer ${key}` } }
        );
        newTabData.usage = res.data.total_usage / 100;
      }

      if (process.env.REACT_APP_SHOW_DETAIL === 'true') {
        const logRes = await API.get(`${baseUrl}/api/log/token`, {
          headers: { Authorization: `Bearer ${key}` },
        });
        const { success, data: logData } = logRes.data;
        if (success) {
          newTabData.logs = logData; // 时间降序
          newTabData.tokenValid = true;
        } else {
          Toast.error('查询调用详情失败，请输入正确的令牌');
        }
      }

      setTabData((prev) => ({ ...prev, [activeTabKey]: newTabData }));
      Toast.success('查询成功');
    } catch (e) {
      Toast.error('查询失败，请输入正确的令牌');
      setTabData((prev) => ({ ...prev, [activeTabKey]: { ...EMPTY_TAB } }));
    } finally {
      setLoading(false);
    }
  };

  // 支持通过 URL 参数直接查询：?key=sk-xxx
  const autoQueried = useRef(false);
  useEffect(() => {
    if (autoQueried.current) return;
    const params = new URLSearchParams(window.location.search);
    const key = params.get('key') || params.get('token');
    if (key) {
      autoQueried.current = true;
      setAPIKey(key);
      fetchData(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- 筛选 ----------

  const allLogs = activeTabData.logs;

  const filteredLogs = useMemo(() => {
    let range = null;
    if (filters.preset === 'custom' && filters.customRange) {
      const [start, end] = filters.customRange;
      range = [start.getTime() / 1000, end.getTime() / 1000 + 86400];
    } else {
      range = getPresetRange(filters.preset);
    }
    const keyword = filters.keyword.trim().toLowerCase();
    const modelSet = filters.models.length > 0 ? new Set(filters.models) : null;

    return allLogs.filter((log) => {
      if (range && (log.created_at < range[0] || log.created_at >= range[1])) return false;
      if (modelSet && !modelSet.has(log.model_name)) return false;
      if (keyword) {
        const haystack = `${log.model_name || ''} ${log.content || ''}`.toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [allLogs, filters]);

  const filteredUsageLogs = useMemo(() => filteredLogs.filter(isUsageLog), [filteredLogs]);

  const totals = useMemo(() => summarize(filteredUsageLogs), [filteredUsageLogs]);
  const days = useMemo(() => groupByDay(filteredUsageLogs), [filteredUsageLogs]);
  const modelStats = useMemo(() => groupByModel(filteredUsageLogs), [filteredUsageLogs]);

  const todayUsd = useMemo(() => {
    const range = getPresetRange('today');
    const quota = filteredUsageLogs
      .filter((l) => l.created_at >= range[0] && l.created_at < range[1])
      .reduce((s, l) => s + (Number(l.quota) || 0), 0);
    return quotaToUSD(quota);
  }, [filteredUsageLogs]);

  const modelOptions = useMemo(() => {
    const names = new Set();
    for (const log of allLogs) {
      if (isUsageLog(log) && log.model_name) names.add(log.model_name);
    }
    return Array.from(names)
      .sort()
      .map((name) => ({ value: name, label: name }));
  }, [allLogs]);

  // ---------- 复制与导出 ----------

  const copyText = async (text) => {
    if (await copy(text)) {
      Toast.success('已复制到剪贴板');
    } else {
      Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
    }
  };

  const copyTokenInfo = () => {
    const { balance, usage, accessdate } = activeTabData;
    const info = `令牌总额: ${balance === 100000000 ? '无限' : Number(balance).toFixed(3)}
剩余额度: ${balance === 100000000 ? '无限制' : (balance - usage).toFixed(3)}
已用额度: ${balance === 100000000 ? '不进行计算' : Number(usage).toFixed(3)}
有效期至: ${accessdate === 0 ? '永不过期' : timestamp2string(accessdate)}`;
    copyText(info);
  };

  const handleExport = (type) => {
    const site = activeTabKey || 'site';
    const stamp = timestampForFile();
    if (type === 'model') {
      downloadCSV(buildModelCSVRows(modelStats), `按模型统计_${site}_${stamp}.csv`);
    } else if (type === 'daily') {
      downloadCSV(buildDailyCSVRows(days), `按日统计_${site}_${stamp}.csv`);
    } else {
      downloadCSV(buildDetailCSVRows(filteredLogs), `调用明细_${site}_${stamp}.csv`);
    }
    Toast.success('已导出（含缓存明细，花费为实际数值）');
  };

  // ---------- 渲染 ----------

  const showBalance = process.env.REACT_APP_SHOW_BALANCE === 'true';
  const showDetail = process.env.REACT_APP_SHOW_DETAIL === 'true';

  return (
    <>
      <div className="hero fade-up">
        <h1 className="hero-title">API 用量查询</h1>
        <p className="hero-sub">输入令牌，查看余额、每日用量趋势与按模型分组统计</p>
        <div className="search-card">
          <Input
            showClear
            size="large"
            value={apikey}
            onChange={setAPIKey}
            placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            prefix={<IconKey style={{ color: 'var(--text-3)' }} />}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchData();
            }}
            aria-label="令牌"
          />
          <Button
            size="large"
            type="primary"
            theme="solid"
            icon={<IconSearch />}
            onClick={fetchData}
            loading={loading}
            disabled={apikey === ''}
            style={{ borderRadius: 12, minWidth: 96 }}
          >
            查询
          </Button>
        </div>
      </div>

      {siteKeys.length > 1 && (
        <Tabs
          type="line"
          className="site-tabs fade-up"
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
        >
          {siteKeys.map((key) => (
            <TabPane tab={key} itemKey={key} key={key} />
          ))}
        </Tabs>
      )}

      {!activeTabData.queried ? (
        <div className="empty-state fade-up fade-up-1">
          <div className="empty-icon">
            <IconHistogram size="extra-large" />
          </div>
          <div className="empty-title">还没有查询数据</div>
          <div className="empty-sub">
            输入令牌并点击查询，即可查看余额信息、日用量图表、按模型统计与调用明细
          </div>
        </div>
      ) : (
        <>
          {showBalance && (
            <TokenInfoCard data={activeTabData} loading={loading} onCopy={copyTokenInfo} />
          )}

          {showDetail && (
            <>
              <FilterBar filters={filters} onChange={setFilters} modelOptions={modelOptions} />
              <StatTiles totals={totals} todayUsd={todayUsd} />
              <UsageTrendPanel days={days} />
              <ModelStatsTable modelStats={modelStats} />
              <DetailTable logs={filteredLogs} loading={loading} onExport={handleExport} />
            </>
          )}
        </>
      )}
    </>
  );
};

export default LogsTable;
